<template>
  <div class="form-container">
    <h3 class="text-center">enterprise {{enterprise.enterpriseId}}</h3>

    <SectionHeader title="data storage"/>
    <form autocomplete="off">

      <div class="form-group">
        <div class="custom-control custom-switch">
          <input type="checkbox" class="custom-control-input" id="dataStorage" v-model="dataStorage">
          <label class="custom-control-label" for="dataStorage">storage enabled</label>
        </div>
      </div>

      <div class="form-group">
        <div class="custom-control custom-switch">
          <input type="checkbox" class="custom-control-input" id="dataDecoding" v-model="dataDecoding">
          <label class="custom-control-label" for="dataDecoding">decoding enabled</label>
        </div>
      </div>

      <button 
        class="btn btn-info new-device-btn"
        type="submit" 
        @click.prevent="updateDataStorage()" 
        :disabled="!canUpdate"
>
        <font-awesome-icon icon="plus"/> update storage options
      </button>

      <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
    </form>

    <SectionHeader title="network server profile" class="mt-5"/>
    <VeeForm v-slot="{ invalid }">

      <form autocomplete="off">

        <div class="form-group">
          <label for="enrolProfile">device enrol profile</label>
          <select class="form-control" v-model="enrolId">
            <option v-for="enrolProfileId of enrolProfileIdList" :key="enrolProfileId" :value="enrolProfileId">{{enrolProfileId}}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="subscriberId">actility subscriber id</label>
          <Field name="subscriberId" rules="required|validSubscriberId" v-slot="{ field, errors }">
            <input type="number" class="form-control" name="subscriberId" placeholder="i.e. 1100000000..." v-bind="field" v-model="subscriberId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="supplierId">actility supplier id</label>
          <Field name="supplierId" rules="validSupplierId" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="supplierId" v-bind="field" v-model="supplierId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <button 
          class="btn btn-info new-device-btn"
          type="submit" 
          @click.prevent="updateNwServer()" 
          :disabled="invalid && !canUpdate"
>
          <font-awesome-icon icon="plus"/> update NS options
        </button>

        <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>

      </form>
    </VeeForm>

    <Error :label="error"/>
    <Success :label="success"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Error from '@/components/Error.vue';
import Success from '@/components/Success.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { Enterprise } from '@/types';
import { ApiClient, curApiClient } from '@/services/ApiClient';

interface Props {
  enterprise: Enterprise;
  existingEntIdList: string[];
  enrolProfileIdList: string[];
}

const props = withDefaults(defineProps<Props>(), {
  enterprise: () => ({}) as Enterprise,
  existingEntIdList: () => [],
  enrolProfileIdList: () => []
});

const emit = defineEmits<{
  cancel: [];
}>();

const router = useRouter();

// our model
const error = ref<string>('');
const success = ref<string>('');

const dataStorage = ref<boolean>(true);
const dataDecoding = ref<boolean>(true);

const subscriberId = ref<number>(0);
const supplierId = ref<string>('');
const enrolId = ref<string>('');

const httpBusy = ref<boolean>(false);

// internal dependencies
const apiClient: ApiClient = curApiClient;

// Watch for enterprise changes
watch(() => props.enterprise, (newValue: Enterprise, _oldValue: Enterprise | undefined) => {
  console.log(`Updating enterprise to ${newValue.enterpriseId}`);

  // we update our model with the start up data
  dataDecoding.value = newValue.dataDecoding;
  dataStorage.value = newValue.dataStorage;

  subscriberId.value = newValue.nwServer && newValue.nwServer.actility && newValue.nwServer.actility.subscriberId ? newValue.nwServer.actility.subscriberId : 0;
  supplierId.value = newValue.nwServer && newValue.nwServer.actility && newValue.nwServer.actility.supplierId ? newValue.nwServer.actility.supplierId : '';
  enrolId.value = newValue.nwServer && newValue.nwServer.actility && newValue.nwServer.actility.enrolId ? newValue.nwServer.actility.enrolId : '';
}, { immediate: true });

onMounted(async () => {
  // Component mounted
});

const canUpdate = computed<boolean>(() => {
  return !httpBusy.value;
});

const updateDataStorage = async () => {
  httpBusy.value = true;
  error.value = '';
  
  const reply = await apiClient.updateEnterpriseDataStorage({
    enterpriseId: props.enterprise.enterpriseId,
    dataStorage: dataStorage.value,
    dataDecoding: dataDecoding.value
  });

  httpBusy.value = false;
  
  if(reply.httpCode === 403)
    return router.push({name:'login'});

  if(reply.errorMsg)
    return error.value = `unable to update data storage options: ${reply.errorMsg}`;

  setSuccess(`data storage for enterprise ${props.enterprise.enterpriseId} updated successfully`);
};

const updateNwServer = async () => {
  httpBusy.value = true;
  error.value = '';
  
  const reply = await apiClient.updateEnterpriseNwServer({
    enterpriseId: props.enterprise.enterpriseId,
    subscriberId: subscriberId.value,
    enrolId: enrolId.value !== '' ? enrolId.value : undefined,
    supplierId: supplierId.value !== '' ? supplierId.value : undefined,
  });

  httpBusy.value = false;
  
  if(reply.httpCode === 403)
    return router.push({name:'login'});

  if(reply.errorMsg)
    return error.value = `unable to update network server options: ${reply.errorMsg}`;

  setSuccess(`network server options for enterprise ${props.enterprise.enterpriseId} updated successfully`);
};

const setSuccess = (str: string) => {
  success.value = str;
  setTimeout(() => emit('cancel'), 1000);
};

const cancel = () => {
  emit('cancel');
};
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

label {
  font-weight: bold;
  color: $bgColour;
}

input::placeholder {
  color: $linkColour;
}

.error {
  color: red;
  font-size: 0.75em;
  font-weight: bold;
  margin-top:10px
}

form button {
  margin: 3px;
}

.tip {
  font-size: 0.7rem;
  font-style: italic;
  color: #999;
}
</style>